import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from '../../../../../../ui/toast';
import { Button } from '../../../../../../ui';
import {
  useCreateOrUpdateCCAConfigMutation,
  useGetCCAConfigQuery,
  useLazyListCCAQuestionsQuery,
  useUpdateCCAConfigQuestionsMutation,
} from '../../../../../../utils/api_service/ccaApi';
import { RootState } from '../../../../../../utils/redux_store/store';
import { CCAQuestion } from '../../../../../../utils/types/cca_types';
import { CCAResultsSection } from './CCAResultsSection';
import { AssignedQuestionsList } from './components/AssignedQuestionsList';
import { ManualSelectionDrawer } from './components/ManualSelectionDrawer';
import { QuickBuildDrawer } from './components/QuickBuildDrawer';

const isLicenseActive = (feature?: { startDate: number | string; endDate: number | string }): boolean => {
  if (!feature) return false;
  return Number(feature.endDate) * 1000 > Date.now();
};

type OpenDrawer = 'quick-build' | 'manual' | null;
interface ICCATabProps {
  setActiveTab: (tab: string) => void;
}

const CCATab: React.FC<ICCATabProps> = ({ setActiveTab }) => {
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);
  const hasCCALicense = isLicenseActive(subscriptions?.hierTalentCca);

  const { id: jobOpeningId = '' } = useParams<{ id: string }>();
  const [openDrawer, setOpenDrawer] = useState<OpenDrawer>(null);
  const [showResults, setShowResults] = useState(false);

  const { data: existingConfig, isLoading: configLoading } =
    useGetCCAConfigQuery(jobOpeningId, { skip: !jobOpeningId });

  const [updateQuestions] = useUpdateCCAConfigQuestionsMutation();
  const [createOrUpdateConfig, { isLoading: isTogglingEnabled }] =
    useCreateOrUpdateCCAConfigMutation();

  const ccaEnabled = existingConfig?.enabled ?? false;

  const handleToggleEnabled = async () => {
    const newEnabled = !ccaEnabled;
    try {
      await createOrUpdateConfig({
        jobOpeningId,
        body: {
          enabled: newEnabled,
          ...(existingConfig?.quickConfig
            ? { quickConfig: existingConfig.quickConfig }
            : {}),
        },
      }).unwrap();
    } catch {
      toast.error(
        `Failed to ${newEnabled ? 'enable' : 'disable'} CCA.`,
        { position: 'bottom-center' },
      );
    }
  };

  // Fetch question texts for the assigned questions list
  const [fetchQuestions] = useLazyListCCAQuestionsQuery();
  const [questionMap, setQuestionMap] = useState<Record<string, CCAQuestion>>(
    {},
  );
  const [isLoadingTexts, setIsLoadingTexts] = useState(false);

  useEffect(() => {
    const assigned = existingConfig?.questions;
    if (!assigned?.length) {
      setQuestionMap({});
      return;
    }
    setIsLoadingTexts(true);
    fetchQuestions({ page: 1, page_size: 500 })
      .unwrap()
      .then((result) => {
        const map: Record<string, CCAQuestion> = {};
        for (const q of result.items) map[q.id] = q;
        setQuestionMap(map);
      })
      .catch(() => {})
      .finally(() => setIsLoadingTexts(false));
  }, [existingConfig?.questions]); // eslint-disable-line

  const ccaConfigured =
    !configLoading &&
    !!existingConfig &&
    ((existingConfig.questions?.length ?? 0) > 0 ||
      (existingConfig.quickConfig?.subdomains?.length ?? 0) > 0);

  const assignedQuestions = existingConfig?.questions ?? [];

  const handleToggleDrawer = (drawer: 'quick-build' | 'manual') => {
    setOpenDrawer((prev) => (prev === drawer ? null : drawer));
    if (showResults) setShowResults(false);
  };

  const handleRemoveQuestion = async (questionId: string) => {
    const remaining = (existingConfig?.questions ?? [])
      .filter((q) => q.questionId !== questionId)
      .map((q, idx) => ({
        questionId: q.questionId,
        orderKey: idx + 1,
        timeLimitSeconds: q.timeLimitSeconds,
      }));
    try {
      await updateQuestions({
        jobOpeningId,
        body: { questions: remaining },
      }).unwrap();
    } catch {
      toast.error('Failed to remove question.', { position: 'bottom-center' });
    }
  };

  const handleClearAll = async () => {
    try {
      await updateQuestions({ jobOpeningId, body: { questions: [] } }).unwrap();
    } catch {
      toast.error('Failed to clear questions.', { position: 'bottom-center' });
    }
  };

  if (!hasCCALicense) {
    return (
      <div className='min-h-[580px] flex items-center justify-center'>
        <div className='text-center max-w-sm'>
          <div className='inline-flex items-center justify-center h-14 w-14 rounded-full bg-slate-100 mb-4'>
            <svg className='h-7 w-7 text-slate-400' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={1.5}>
              <path strokeLinecap='round' strokeLinejoin='round' d='M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z' />
            </svg>
          </div>
          <h3 className='text-sm font-semibold text-slate-800 mb-1'>HierTalent CCA License Required</h3>
          <p className='text-xs text-slate-500 mb-4'>
            Your organization does not have an active CCA license. Contact your account manager to enable Candidate Competency Assessments.
          </p>
          <a
              href='mailto:sales@hiacuity.com'
              className='inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 transition-colors'
          >
            Contact Sales
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-[580px]'>
      { /* ── Top bar ─────────────────────────────────────────────── */ }
      <div className='mb-5 flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          { /* Enable / Disable toggle */ }
          <button
              type='button'
              role='switch'
              aria-checked={ccaEnabled}
              onClick={handleToggleEnabled}
              disabled={configLoading || isTogglingEnabled}
              className={`relative inline-flex h-4 w-8 shrink-0 items-center rounded-full transition-colors ${
                ccaEnabled
                  ? 'bg-gradient-to-r from-emerald-700 to-emerald-500'
                  : 'bg-gray-200'
              } ${configLoading || isTogglingEnabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
                className={`inline-block h-3 w-3 transform rounded-full bg-white shadow-md transition-transform ${
                  ccaEnabled ? 'translate-x-4' : 'translate-x-1'
                }`}
            />
          </button>
          <span
              className={`text-sm font-semibold ${
                ccaEnabled ? 'text-emerald-700' : 'text-slate-500'
              }`}
          >
            { configLoading
              ? 'Loading…'
              : ccaEnabled
                ? 'CCA Enabled'
                : 'CCA Disabled' }
          </span>
          { !configLoading && !ccaEnabled && (
            <span className='text-xs text-slate-400'>
              Enable CCA and configure questions to assess candidates.
            </span>
          ) }
        </div>

        { /* Action buttons */ }
        <div className='flex items-center gap-2'>
          { ccaConfigured && (
            <button
                type='button'
                onClick={() => {
                  setShowResults((v) => !v);
                  setOpenDrawer(null);
                }}
                className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                  showResults
                    ? 'bg-slate-800 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
            >
              <svg
                  className='h-3.5 w-3.5'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  strokeWidth={2}
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z'
                />
              </svg>
              Results
            </button>
          ) }
          <button
              type='button'
              onClick={() => handleToggleDrawer('quick-build')}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                openDrawer === 'quick-build'
                  ? 'bg-emerald-600 text-white shadow-sm'
                  : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
              }`}
          >
            <svg
                className='h-3.5 w-3.5'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M13 10V3L4 14h7v7l9-11h-7z'
              />
            </svg>
            Quick Build
          </button>
          <button
              type='button'
              onClick={() => handleToggleDrawer('manual')}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                openDrawer === 'manual'
                  ? 'bg-indigo-600 text-white shadow-sm'
                  : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'
              }`}
          >
            <svg
                className='h-3.5 w-3.5'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                strokeWidth={2}
            >
              <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M4 6h16M4 10h16M4 14h10M4 18h6'
              />
            </svg>
            Manual Selection
          </button>
        </div>
      </div>

      { /* ── Main content ─────────────────────────────────────────── */ }
      { showResults ? (
        <CCAResultsSection jobOpeningId={jobOpeningId} />
      ) : (
        <AssignedQuestionsList
            assignedQuestions={assignedQuestions}
            questionMap={questionMap}
            isLoading={configLoading || isLoadingTexts}
            onRemove={handleRemoveQuestion}
            onClearAll={assignedQuestions.length > 0 ? handleClearAll : undefined}
        />
      ) }
      <div className='flex justify-between items-center mt-6 pt-6 border-t border-slate-100'>
        <button
            onClick={() => setActiveTab('preQualifyingQuestions')}
            className='text-sm font-medium text-slate-400 hover:text-slate-600 transition-colors'
        >
          &larr; Back to Pre-Qualifying Questions
        </button>
        <Button
            onClick={() => setActiveTab('preview')}
            variant='primary'
            label='Next'
            className='shadow-lg shadow-indigo-500/20 px-10'
        />
      </div>
      { /* ── Quick Build Drawer ───────────────────────────────────── */ }
      <QuickBuildDrawer
          isOpen={openDrawer === 'quick-build'}
          onClose={() => setOpenDrawer(null)}
          jobOpeningId={jobOpeningId}
          existingConfig={existingConfig}
      />

      { /* ── Manual Selection Drawer ──────────────────────────────── */ }
      <ManualSelectionDrawer
          isOpen={openDrawer === 'manual'}
          onClose={() => setOpenDrawer(null)}
          jobOpeningId={jobOpeningId}
          existingConfig={existingConfig}
      />
    </div>
  );
};

export default CCATab;
